import { createDeepEqualSelector } from "../utils/createDeepEqualSelector";
import { selectAllRecipients } from "../recipients/selectors";
import { selectAllAccounts } from "../accounts";

import { formatPaymentActivity } from "./helpers";
import { EMPTY_FILTER_QUERY } from "./consts";

import type { RootState } from "../store";

export const selectPaymentActivityState = (state: RootState) =>
  state.paymentActivity;

export const selectById = createDeepEqualSelector(
  selectPaymentActivityState,
  (state) => state.byId,
);

export const selectPageMap = createDeepEqualSelector(
  selectPaymentActivityState,
  (state) => state.pageMap,
);

const selectList = createDeepEqualSelector(
  selectPaymentActivityState,
  (state) => state.list,
);

const selectSelected = createDeepEqualSelector(
  selectPaymentActivityState,
  (state) => state.selected,
);

export const selectTotal = (state: RootState) => state.paymentActivity.total;

export const selectPaginatedPaymentActivity = createDeepEqualSelector(
  [selectPageMap, selectById, (_, pageNumber: number) => pageNumber],
  (pageMap, byId, pageNumber) => {
    const ids = pageMap[pageNumber];
    return ids?.map((id) => byId[id]).filter(Boolean) ?? [];
  },
);

export const selectPaymentActivity = createDeepEqualSelector(
  [selectList, selectById, selectAllRecipients, selectAllAccounts],
  (list, byId, recipients, accounts) => {
    // list can rehydrate as undefined from persisted state that predates this field
    return (list ?? [])
      .map((id) => {
        const activity = byId[id];
        if (!activity) return null;
        return formatPaymentActivity(activity, recipients, accounts);
      })
      .filter((activity): activity is API.FormattedPaymentActivity =>
        Boolean(activity),
      );
  },
);

export const selectFormattedPaginatedPaymentActivity = createDeepEqualSelector(
  [
    selectPageMap,
    selectById,
    selectAllRecipients,
    selectAllAccounts,
    (_, pageNumber: number) => pageNumber,
  ],
  (pageMap, byId, recipients, accounts, pageNumber) => {
    const ids = pageMap[pageNumber] ?? [];

    return ids
      ?.map((id) => {
        const activity = byId[id];
        if (!activity) return null;
        return formatPaymentActivity(activity, recipients, accounts);
      })
      ?.filter((activity): activity is API.FormattedPaymentActivity =>
        Boolean(activity),
      );
  },
);

export const selectSelectedPaymentActivity = createDeepEqualSelector(
  [selectSelected, selectAllRecipients, selectAllAccounts],
  (activity, recipients, accounts) => {
    if (!activity) return null;
    return formatPaymentActivity(activity, recipients, accounts);
  },
);

export const selectFilters = createDeepEqualSelector(
  selectPaymentActivityState,
  (state) => state?.filters || EMPTY_FILTER_QUERY,
);
